// Measurement conversion utilities for birth measurements

/**
 * Convert kilograms to pounds and ounces
 * @param {number} kg - Weight in kilograms
 * @returns {{lbs: number, oz: number}} - Weight in pounds and ounces
 */
export function convertKgToLbsOz(kg) {
  if (!kg || isNaN(kg)) return { lbs: 0, oz: 0 };
  
  const totalPounds = kg * 2.20462;
  const lbs = Math.floor(totalPounds);
  const oz = (totalPounds - lbs) * 16;
  
  return {
    lbs,
    oz: Math.round(oz * 100) / 100 // Round to 2 decimal places
  };
}

/**
 * Convert pounds and ounces to kilograms
 * @param {number} lbs - Weight in pounds
 * @param {number} oz - Weight in ounces
 * @returns {number} - Weight in kilograms
 */
export function convertLbsOzToKg(lbs, oz) {
  const pounds = parseFloat(lbs || 0);
  const ounces = parseFloat(oz || 0);
  
  if (isNaN(pounds) && isNaN(ounces)) return 0;
  
  const totalPounds = pounds + (ounces / 16);
  const kg = totalPounds / 2.20462;
  
  return Math.round(kg * 100) / 100; // Round to 2 decimal places
}

const asFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/** Format a gram measurement for display without changing its stored precision. */
export function formatWholeGrams(value, fallback = '--') {
  const grams = asFiniteNumber(value);
  return grams === null ? fallback : String(Math.round(grams));
}

/** Convert pounds and ounces to canonical whole grams. */
export function convertLbsOzToGrams(lbs, oz) {
  const pounds = asFiniteNumber(lbs);
  const ounces = asFiniteNumber(oz);

  if (pounds === null && ounces === null) return null;
  return Math.round(((pounds ?? 0) * 453.59237) + ((ounces ?? 0) * 28.349523125));
}

/** Convert grams to pounds and ounces, matching Newborn Visit's tenth-ounce display. */
export function convertGramsToLbsOz(grams) {
  const weightInGrams = asFiniteNumber(grams);
  if (weightInGrams === null) return { lbs: null, oz: null };

  const totalOunces = weightInGrams / 28.349523125;
  let lbs = Math.floor(totalOunces / 16);
  let oz = Math.round((totalOunces % 16) * 10) / 10;

  // Prevent rounding from producing an invalid 16 oz display.
  if (oz === 16) {
    lbs += 1;
    oz = 0;
  }

  return { lbs, oz };
}

/**
 * Normalize the API's canonical gram value and legacy/display weight fields.
 * Existing records may contain any combination of camelCase/PascalCase grams,
 * kilograms, or pounds/ounces, so reads must not depend on the active UI unit.
 */
export function normalizeNewbornWeight(baby = {}, birthEvent = {}) {
  const read = (source, camel, pascal) => asFiniteNumber(source?.[camel] ?? source?.[pascal]);
  const storedGrams = read(baby, 'weightInGrams', 'WeightInGrams')
    ?? read(birthEvent, 'weightInGrams', 'WeightInGrams');
  const storedKg = read(baby, 'weightKg', 'WeightKg')
    ?? read(birthEvent, 'weightKg', 'WeightKg');
  const storedLbs = read(baby, 'weightLbs', 'WeightLbs')
    ?? read(birthEvent, 'weightLbs', 'WeightLbs');
  const storedOz = read(baby, 'weightOz', 'WeightOz')
    ?? read(birthEvent, 'weightOz', 'WeightOz');

  const weightInGrams = storedGrams
    ?? (storedKg !== null ? Math.round(storedKg * 1000) : null)
    ?? (storedLbs !== null || storedOz !== null
      ? convertLbsOzToGrams(storedLbs, storedOz)
      : null);

  if (weightInGrams === null) {
    return { weightInGrams: null, weightKg: null, weightLbs: null, weightOz: null };
  }

  const { lbs: derivedLbs, oz: derivedOz } = convertGramsToLbsOz(weightInGrams);
  const imperialIsOnlyAvailableSource = storedGrams === null && storedKg === null;

  return {
    weightInGrams,
    weightKg: weightInGrams / 1000,
    weightLbs: imperialIsOnlyAvailableSource ? (storedLbs ?? derivedLbs) : derivedLbs,
    weightOz: imperialIsOnlyAvailableSource ? (storedOz ?? derivedOz) : derivedOz,
  };
}

/** Return canonical whole grams using the unit the clinician is editing. */
export function getNewbornWeightInGrams(baby = {}, unit = 'US') {
  const grams = asFiniteNumber(baby.weightInGrams ?? baby.WeightInGrams);
  const kg = asFiniteNumber(baby.weightKg ?? baby.WeightKg);
  const lbs = asFiniteNumber(baby.weightLbs ?? baby.WeightLbs);
  const oz = asFiniteNumber(baby.weightOz ?? baby.WeightOz);

  // Both editable modes update grams immediately, so a canonical value always
  // wins over rounded display fields when the user only switches units.
  if (grams !== null) return Math.round(grams);
  // Transitional state from records loaded before the grams UI was introduced.
  if (unit === 'Metric' && kg !== null) return Math.round(kg * 1000);
  if (unit === 'US' && (lbs !== null || oz !== null)) {
    return convertLbsOzToGrams(lbs, oz);
  }

  return normalizeNewbornWeight(baby).weightInGrams;
}

/**
 * Convert centimeters to inches
 * @param {number} cm - Length in centimeters
 * @returns {number} - Length in inches
 */
export function convertCmToInches(cm) {
  if (!cm || isNaN(cm)) return 0;
  
  const inches = cm / 2.54;
  return Math.round(inches * 100) / 100; // Round to 2 decimal places
}

/**
 * Convert inches to centimeters
 * @param {number} inches - Length in inches
 * @returns {number} - Length in centimeters
 */
export function convertInchesToCm(inches) {
  if (!inches || isNaN(inches)) return 0;
  
  const cm = inches * 2.54;
  return Math.round(cm * 100) / 100; // Round to 2 decimal places
}
